import React, { useState, useEffect, act } from 'react';
import { Table, Row, Col, Typography, Button, Tooltip, message, Modal, Tag, Checkbox, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import CreateApprovalProcessDrawer from './CreateApprovalProcessDrawer';
import ApiService from '../Components/apiService';
import { BASE_URL,DateFormat,DateTimeFormat } from '../Components/Constant';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {convertDateToUserTimezone} from '../Components/Utility';

const { Title } = Typography;

const ApprovalProcess = ({ object }) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [approvalProcesses, setApprovalProcesses] = useState([]);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
  const [processToDelete, setProcessToDelete] = useState(null);
  const security = useSelector((state) => state.data.items[0]);
  const [pageSize, setPageSize] = useState(10);

  // console.log("SECURITY:", security?.system_settings.approval_process.create);

  useEffect(() => {
    if(security?.system_settings?.approval_process?.view===false){
      message.error('You dont have permission to view ');
      return;
    }
    fetchApprovalProcesses();
  }, []);


  const fetchApprovalProcesses = async () => {
    try {
      const apiServiceForLookup = new ApiService(
        `${BASE_URL}/approval_processes`,
        { 'Content-Type': 'application/json' },
        'GET'
      );
      
      const response = await apiServiceForLookup.makeCall();

      if (Array.isArray(response)) {
        const processes = response.map((process) => ({
          key: process._id,
          name: process.name,
          objectName: process.object_name,
          active: process.active, // Map active
          createdDate: new Date(process.created_at).toLocaleString(),
          ...process,
        }));

        setApprovalProcesses(processes);
      } else {
        message.warning('No approval processes found.');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleEditClick = (process) => {
    if(security?.system_settings?.approval_process?.edit===false){
          message.error('You do not have permission to edit ');
          return;
        }
    setSelectedProcess(process);
    setDrawerVisible(true);
  };

  const deleteApprovalProcess = async (process) => {
    if(security?.system_settings?.approval_process?.delete===false){
      message.error('You do not have permission to delete ');
      return;
    }
    try {
      // Create ApiService instance for DELETE request
      const apiService = new ApiService(
        `${BASE_URL}/approval_processes/${process._id}`,
        { 'Content-Type': 'application/json' },
        'DELETE'
      );
      await apiService.makeCall();
      fetchApprovalProcesses();

      message.success('Approval process deleted successfully.');
    } catch (error) {
      message.error('Failed to delete approval process.');
      console.error('Error deleting approval process:', error);
    }
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedProcess(null);
  };

  const columns = [
    {
      title: 'Approval Process Name',
      dataIndex: 'Name',
      key: 'Name',
      sorter: (a, b) => a.Name.localeCompare(b.Name),
      render: (text, record) => (
        <a
          onClick={() => handleEditClick(record)}
          style={{ color: '#1890ff', textDecoration: 'underline', cursor: 'pointer' }}
        >
          {text}
        </a>
      ),
    },
    {
       title: 'Object',
       dataIndex: 'objectName',
       key: 'objectName',
       sorter: (a, b) => a.objectName.localeCompare(b.object),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      render: (active) => <Checkbox checked={active} disabled />,

    },
    // {
    //   title: 'Created Date',
    //   dataIndex: 'createdDate',
    //   key: 'createdDate',
    //   sorter: (a, b) => new Date(a.createdDate) - new Date(b.createdDate),
    // },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      // width: 100,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (text) => (
        text && dayjs(text).isValid()
          ? convertDateToUserTimezone(text, DateTimeFormat)
          : ''
      ),      
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      // width: 100,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (text) => (
        text && dayjs(text).isValid()
          ? convertDateToUserTimezone(text, DateTimeFormat)
          : ''
      ),      
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      render: (_, record) => (
        <>
          <Tooltip title="Edit">
            <EditOutlined
              style={{ cursor: 'pointer', color: 'blue', marginRight: 10 }}
              onClick={() => handleEditClick(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
          <Popconfirm
            title="Are you sure you want to delete this item?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => {
              deleteApprovalProcess(record)
            }
            }

          >
            <DeleteOutlined style={{ color: 'red', marginRight: 8, fontSize: '14px', cursor: 'pointer' }} />
          </Popconfirm>
        </Tooltip>
         
        </>
      ),
    },
  ];

  return (
    <div>
      {!drawerVisible && (
        <>
          {/* Active Approval Process Section */}
          <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                Approval Processes
              </Title>
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={() =>{
                  if(security?.system_settings?.approval_process.create===false){
                     message.error('You dont have permission to create ');
                     return;
                     }  
                  setDrawerVisible(true)}
               }
              >
                Create Approval Process
              </Button>
            </Col>
          </Row>

          {/* Table Section */}
          <Table
            columns={columns}
            dataSource={approvalProcesses}
            scroll={{ x: 'max-content', y: 550 }}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPageSize(pageSize);
              },              
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </>
      )}

      {drawerVisible && (
        <CreateApprovalProcessDrawer
          visible={drawerVisible}
          onClose={closeDrawer}
          object={object}
          process={selectedProcess}
          fetchApprovalProcess={fetchApprovalProcesses}
        />
      )}

    </div>
  );
};

export default ApprovalProcess;
